import { call, all, select, takeEvery } from "redux-saga/effects";
import { authorizationSagas } from "store/features/authorization";
import {
    deleteSchedulerRule,
    fetchSchedulerOverviewConfigs,
    deleteEmailSchedulerOverviewSaga,
    fetchEmailSchedulerOverviewSaga,
} from "store/features/emailScheduler/overview";
import { riseError, globalErrorHandlerSaga } from "store/features/general";
import {
    createSchedulerRule,
    fetchSchedulerDefinitions,
    updateSchedulerRule,
    fetchEmailSchedulerDefinitionSaga,
    createEmailSchedulerRuleSaga,
    updateEmailSchedulerRuleSaga,
} from "store/features/emailScheduler/definition";
import {
    createSchedulerRuleException,
    deleteSchedulerRuleException,
    fetchSchedulerRuleExceptions,
    updateSchedulerRuleException,
    createEmailSchedulerRuleExceptionSaga,
    deleteSchedulerRuleExceptionSaga,
    fetchEmailSchedulerRuleExceptionSaga,
    updateEmailSchedulerRuleExceptionSaga,
} from "store/features/emailScheduler/exceptions";
import {
    deleteTemplate,
    fetchTemplates,
    deleteTemplateSaga,
    fetchTemplatesSaga,
} from "store/features/templates/overivew";
import {
    fetchTemplateDetails,
    updateTemplate,
    validateTemplate,
    fetchTemplateDetailsSaga,
    updateTemplateSaga,
    validateTemplateSaga,
    createTemplateSaga,
    createTemplate,
} from "store/features/templates/definition";
import { getAuthorizationToken } from "store/features/authorization";
import {
    createCreativeMapping,
    createCreativeMappingSaga,
    deleteCreativeMapping,
    deleteCreativeMappingSaga,
    fetchCreativeMappings,
    fetchCreativeMappingsSaga,
    updateCreativeMapping,
    updateCreativeMappingSaga,
} from "store/features/creativeMappings/overivew";
import { fetchCreativeMappingsFields, fetchCreativeMappingsFieldsSaga } from "store/features/filters";
import {
    fetchBookings,
    fetchBookingsSaga,
    uploadMaterials,
    uploadMaterialsSaga,
    fetchThirdPartyTag,
    fetchThirdPartyTagSaga,
    submitThirdPartyTag,
    submitThirdPartyTagSaga,
    submitNewCreativeThirdPartyTag,
    submitNewCreativeThirdPartyTagSaga,
    fetchValidationReport,
    fetchValidationReportSaga,
    removeMaterial,
    removeMaterialSaga,
    fetchPrintValidationReport,
    fetchPrintValidationReportSaga,
    updateMaterialSpecStatus,
    updateMaterialSpecStatusSaga,
    submitPrintMaterialDescriptionSaga,
    submitPrintMaterialDescription,
    submitPrintMaterialAdServerProperties,
    submitPrintMaterialAdServerPropertiesSaga,
    updateExistingSpecPropertiesSaga,
    updateExistingSpecProperties,
} from "store/features/materialSpecs/specs";
import {
    removeBriefFormAsset,
    submitBriefForm,
    uploadBriefFormAsset,
    fetchBriefForm,
    uploadBriefFormAdditionalAssets,
} from "./features/briefForm/briefForm.actions";
import {
    removeBriefFormAssetSaga,
    submitBriefFormSaga,
    uploadBriefFormAssetSaga,
    fetchBriefFormSaga,
    uploadBriefFormAdditionalAssetsSaga,
} from "./features/briefForm/briefForm.sagas";
import { fetchAd } from "./features/materialSpecs/ad/ad.actions";
import { fetchAdSaga } from "./features/materialSpecs/ad/ad.sagas";
import { getUrlParam } from "utils";
import {
    fetchAnnotations,
    removeAnnotation,
    submitAnnotations,
    submitIsAnnotationReviewed,
    attachAssetToAnnotation,
    removeAnnotationAsset,
} from "./features/annotations/annotations.actions";
import {
    fetchAnnotationsSaga,
    removeAnnotationSaga,
    submitAnnotationsSaga,
    submitIsAnnotationReviewedSaga,
    attachAssetToAnnotationSaga,
    removeAnnotationAssetSaga,
} from "./features/annotations/annotations.sagas";

export function* rootSaga(): Generator {
    const jwtToken = getUrlParam("jwt");

    yield call(authorizationSagas, jwtToken);
    const token = yield select(getAuthorizationToken);

    const headers: HeadersInit = {
        Authorization: `Bearer ${token}`,
    };

    yield all([
        takeEvery(fetchSchedulerOverviewConfigs.request, fetchEmailSchedulerOverviewSaga, headers),
        takeEvery(fetchSchedulerDefinitions.request, fetchEmailSchedulerDefinitionSaga, headers),
        takeEvery(createSchedulerRule.request, createEmailSchedulerRuleSaga, headers),
        takeEvery(updateSchedulerRule.request, updateEmailSchedulerRuleSaga, headers),
        takeEvery(createSchedulerRuleException.request, createEmailSchedulerRuleExceptionSaga, headers),
        takeEvery(updateSchedulerRuleException.request, updateEmailSchedulerRuleExceptionSaga, headers),
        takeEvery(fetchSchedulerRuleExceptions.request, fetchEmailSchedulerRuleExceptionSaga, headers),
        takeEvery(deleteSchedulerRule.request, deleteEmailSchedulerOverviewSaga, headers),
        takeEvery(deleteSchedulerRuleException.request, deleteSchedulerRuleExceptionSaga, headers),
        takeEvery(fetchTemplates.request, fetchTemplatesSaga, headers),
        takeEvery(fetchTemplateDetails.request, fetchTemplateDetailsSaga, headers),
        takeEvery(updateTemplate.request, updateTemplateSaga, headers),
        takeEvery(createTemplate.request, createTemplateSaga, headers),
        takeEvery(deleteTemplate.request, deleteTemplateSaga, headers),
        takeEvery(validateTemplate.request, validateTemplateSaga, headers),
        takeEvery(fetchCreativeMappings.request, fetchCreativeMappingsSaga, headers),
        takeEvery(fetchCreativeMappingsFields.request, fetchCreativeMappingsFieldsSaga, headers),
        takeEvery(createCreativeMapping.request, createCreativeMappingSaga, headers),
        takeEvery(updateCreativeMapping.request, updateCreativeMappingSaga, headers),
        takeEvery(deleteCreativeMapping.request, deleteCreativeMappingSaga, headers),
        takeEvery(fetchBookings.request, fetchBookingsSaga, headers),
        takeEvery(fetchThirdPartyTag.request, fetchThirdPartyTagSaga, headers),
        takeEvery(uploadBriefFormAsset.request, uploadBriefFormAssetSaga, headers),
        takeEvery(uploadBriefFormAdditionalAssets.request, uploadBriefFormAdditionalAssetsSaga, headers),
        takeEvery(submitBriefForm.request, submitBriefFormSaga, headers),
        takeEvery(fetchBriefForm.request, fetchBriefFormSaga, headers),
        takeEvery(fetchAd.request, fetchAdSaga, headers),
        takeEvery(submitThirdPartyTag.request, submitThirdPartyTagSaga, headers),
        takeEvery(submitNewCreativeThirdPartyTag.request, submitNewCreativeThirdPartyTagSaga, headers),
        takeEvery(uploadMaterials.request, uploadMaterialsSaga, headers),
        takeEvery(fetchValidationReport.request, fetchValidationReportSaga, headers),
        takeEvery(fetchPrintValidationReport.request, fetchPrintValidationReportSaga, headers),
        takeEvery(fetchAnnotations.request, fetchAnnotationsSaga, headers),
        takeEvery(submitAnnotations.request, submitAnnotationsSaga, headers),
        takeEvery(submitIsAnnotationReviewed.request, submitIsAnnotationReviewedSaga, headers),
        takeEvery(removeAnnotation.request, removeAnnotationSaga, headers),
        takeEvery(attachAssetToAnnotation.request, attachAssetToAnnotationSaga, headers),
        takeEvery(removeAnnotationAsset.request, removeAnnotationAssetSaga, headers),
        takeEvery(removeBriefFormAsset.request, removeBriefFormAssetSaga, headers),
        takeEvery(removeMaterial.request, removeMaterialSaga, headers),
        takeEvery(updateMaterialSpecStatus.request, updateMaterialSpecStatusSaga, headers),
        takeEvery(submitPrintMaterialDescription.request, submitPrintMaterialDescriptionSaga, headers),
        takeEvery(submitPrintMaterialAdServerProperties.request, submitPrintMaterialAdServerPropertiesSaga, headers),
        takeEvery(updateExistingSpecProperties.request, updateExistingSpecPropertiesSaga, headers),
        takeEvery(riseError, globalErrorHandlerSaga),
    ]);
}
